import MaskAndValid from "utils/maskAndValid";

export function unwrapApiResult(data) {
  if (!data) return null;
  return data.Result !== undefined ? data.Result : data.result;
}

/** Documento da pessoa (CPF 11 ou CNPJ 14 dígitos) para rótulo no formulário. */
function formatCpfCnpjExibicao(raw) {
  if (raw == null || raw === "") return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 11) return MaskAndValid.maskCPF(digits);
  if (digits.length === 14) return MaskAndValid.maskCNPJ(digits);
  if (digits.length > 0) return digits;
  return String(raw).trim();
}

/**
 * Opção do autocomplete "Razão social": id estável `razaoSocial` (texto) e rótulo "nome | documento"
 * (formato esperado por `ignoreCnpjPunctuationInSearch` no Autocomplete).
 */
export function buildRazaoSocialOption(razaoSocial, cnpjRaw) {
  const razao = (razaoSocial ?? "").trim();
  const doc = formatCpfCnpjExibicao(cnpjRaw);
  return {
    razaoSocial: razao,
    razaoSocialLabel: doc ? `${razao} | ${doc}` : razao,
  };
}

/** Garante texto de grupo para UI e `grupoEconomico` vazio (envio como null na API). */
export function normalizeLojaForm(loja) {
  if (!loja) return null;
  const g = String(loja.grupoEconomico ?? "").trim();
  return {
    ...loja,
    grupoEconomico: g,
    grupoEconomicoExibicao: g || "—",
  };
}

export function mapLojaFromApi(d) {
  const id = d.id ?? d.Id;
  const razaoSocial = (d.razaoSocial ?? d.RazaoSocial ?? "").trim();
  const cnpjRaw = d.cnpj ?? d.Cnpj ?? d.CNPJ ?? "";
  return normalizeLojaForm({
    id: id != null ? String(id) : "",
    idPessoaLoja: d.idPessoaLoja ?? d.IdPessoaLoja,
    idGrupoEconomico: d.idGrupoEconomico ?? d.IdGrupoEconomico,
    razaoSocial,
    cnpj: String(cnpjRaw ?? "").trim(),
    grupoEconomico: (d.grupoEconomico ?? d.GrupoEconomico ?? "").trim(),
    cidade: d.cidade ?? d.Cidade ?? "",
    uf: d.uf ?? d.Uf ?? "",
  });
}

export function mapProdutoReferenciaFromApi(d) {
  const id = d.id ?? d.Id;
  const pk = d.precoKarsten ?? d.PrecoKarsten;
  const planejadosRaw =
    d.concorrentesPlanejados ?? d.ConcorrentesPlanejados ?? [];
  const concorrentesPlanejados = Array.isArray(planejadosRaw)
    ? planejadosRaw.map((p, idx) => ({
        nome: p.nome ?? p.Nome ?? "",
        produtoReferencia: p.produtoReferencia ?? p.ProdutoReferencia ?? null,
        ordem: p.ordem ?? p.Ordem ?? idx,
      }))
    : [];
  const nomesApi = d.concorrentesNomes ?? d.ConcorrentesNomes ?? [];
  const concorrentesNomes =
    nomesApi && nomesApi.length
      ? nomesApi
      : concorrentesPlanejados.map((p) => p.nome).filter(Boolean);
  return {
    id: id != null ? String(id) : "",
    sku: d.sku ?? d.Sku ?? "",
    nome: d.nome ?? d.Nome ?? "",
    linha: d.linha ?? d.Linha ?? "",
    categoria: d.categoria ?? d.Categoria ?? "",
    idCategoria: d.idCategoria ?? d.IdCategoria,
    categoriaCatalogo: d.categoriaCatalogo ?? d.CategoriaCatalogo ?? null,
    gramatura: d.gramatura ?? d.Gramatura ?? "",
    tecidoBase: d.tecidoBase ?? d.TecidoBase ?? "",
    listaSkuNome: d.listaSkuNome ?? d.ListaSkuNome ?? "",
    precoKarsten: pk != null ? Number(pk) : null,
    imagemUrl: d.imagemUrl ?? d.ImagemUrl ?? "",
    concorrentesNomes,
    concorrentesPlanejados,
  };
}

/** Loja no POST — só campos do contrato da API (camelCase). */
export function mapLojaToApiPayload(loja) {
  if (!loja) return null;
  const grupo = loja.grupoEconomico;
  const grupoTrim =
    grupo == null || typeof grupo !== "string"
      ? ""
      : String(grupo).trim();
  return {
    id: loja.id ?? null,
    razaoSocial: loja.razaoSocial ?? null,
    grupoEconomico: grupoTrim === "" ? null : grupoTrim,
    cidade: loja.cidade ?? null,
    uf: loja.uf ?? null,
    idPessoaLoja: loja.idPessoaLoja ?? null,
    idGrupoEconomico: loja.idGrupoEconomico ?? null,
  };
}

/** Produto referência no POST. */
export function mapProdutoToApiPayload(produto) {
  if (!produto) return null;
  return {
    id: produto.id ?? null,
    sku: produto.sku ?? null,
    nome: produto.nome ?? null,
    linha: produto.linha ?? null,
    categoria: produto.categoria ?? null,
    gramatura: produto.gramatura ?? null,
    tecidoBase: produto.tecidoBase ?? null,
    imagemUrl: produto.imagemUrl ?? null,
  };
}

/** Concorrente no POST: API infere “encontrado” por `precoNormal != null`. */
export function mapConcorrenteRowToApi(r) {
  if (!r.encontrado) {
    return {
      nome: r.nome,
      precoNormal: null,
      emPromocao: false,
      precoPromocional: null,
      fotoBase64: null,
    };
  }
  return {
    nome: r.nome,
    precoNormal: r.precoNormal ?? null,
    emPromocao: !!r.emPromocao,
    precoPromocional: r.emPromocao ? (r.precoPromocional ?? null) : null,
    fotoBase64: r.fotoBase64 || null,
  };
}

export function initialConcorrenteRow(nome, produtoReferencia = "") {
  return {
    nome,
    produtoReferencia: produtoReferencia || "",
    encontrado: true,
    precoNormal: undefined,
    emPromocao: false,
    precoPromocional: undefined,
    fotoBase64: "",
  };
}

export function mapColetaHistoricoFromApi(d) {
  if (!d) return null;
  const lojaRaw = d.loja ?? d.Loja ?? {};
  const prodRaw = d.produto ?? d.Produto ?? {};
  const concRaw = d.concorrentes ?? d.Concorrentes ?? [];
  const mapConc = (c) => ({
    nome: c.nome ?? c.Nome ?? "",
    precoNormal:
      c.precoNormal != null
        ? Number(c.precoNormal)
        : c.PrecoNormal != null
          ? Number(c.PrecoNormal)
          : null,
    emPromocao: !!(c.emPromocao ?? c.EmPromocao),
    precoPromocional:
      c.precoPromocional != null
        ? Number(c.precoPromocional)
        : c.PrecoPromocional != null
          ? Number(c.PrecoPromocional)
          : null,
    temFoto: !!(c.temFoto ?? c.TemFoto),
    encontrado: !!(c.encontrado ?? c.Encontrado),
  });
  return {
    idColeta: d.idColeta ?? d.IdColeta,
    id: d.id != null ? String(d.id) : d.Id != null ? String(d.Id) : "",
    tipoColeta: d.tipoColeta ?? d.TipoColeta ?? "",
    dataColeta: d.dataColeta ?? d.DataColeta ?? "",
    representante: d.representante ?? d.Representante ?? "",
    dataEnvioUtc: d.dataEnvioUtc ?? d.DataEnvioUtc,
    loja: mapLojaFromApi(lojaRaw),
    produto: {
      id:
        prodRaw.id != null
          ? String(prodRaw.id)
          : prodRaw.Id != null
            ? String(prodRaw.Id)
            : "",
      sku: prodRaw.sku ?? prodRaw.Sku ?? "",
      nome: prodRaw.nome ?? prodRaw.Nome ?? "",
      linha: prodRaw.linha ?? prodRaw.Linha ?? "",
      categoria: prodRaw.categoria ?? prodRaw.Categoria ?? "",
      gramatura: prodRaw.gramatura ?? prodRaw.Gramatura ?? "",
      tecidoBase: prodRaw.tecidoBase ?? prodRaw.TecidoBase ?? "",
      imagemUrl: prodRaw.imagemUrl ?? prodRaw.ImagemUrl ?? "",
    },
    precoKarstenLoja: Number(d.precoKarstenLoja ?? d.PrecoKarstenLoja ?? 0),
    concorrentes: Array.isArray(concRaw) ? concRaw.map(mapConc) : [],
  };
}

export function mapColetasHistoricoPageFromApi(data) {
  if (!data) {
    return {
      items: [],
      totalCount: 0,
      page: 1,
      pageSize: 15,
      totalPages: 0,
    };
  }
  const itemsRaw = data.items ?? data.Items ?? [];
  const totalCount = Number(data.totalCount ?? data.TotalCount ?? 0);
  const page = Number(data.page ?? data.Page ?? 1);
  const pageSize = Number(data.pageSize ?? data.PageSize ?? 15);
  const totalPages = Number(data.totalPages ?? data.TotalPages ?? 0);
  return {
    items: Array.isArray(itemsRaw)
      ? itemsRaw.map(mapColetaHistoricoFromApi).filter(Boolean)
      : [],
    totalCount: Number.isNaN(totalCount) ? 0 : totalCount,
    page: Number.isNaN(page) || page < 1 ? 1 : page,
    pageSize: Number.isNaN(pageSize) || pageSize < 1 ? 15 : pageSize,
    totalPages: Number.isNaN(totalPages) ? 0 : totalPages,
  };
}

export function buildConcorrentesRowsFromProduto(full) {
  const planejados =
    full.concorrentesPlanejados ?? full.ConcorrentesPlanejados ?? [];
  if (Array.isArray(planejados) && planejados.length > 0) {
    const sorted = [...planejados].sort(
      (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0),
    );
    return sorted.map((p) =>
      initialConcorrenteRow(
        p.nome ?? p.Nome ?? "",
        p.produtoReferencia ?? p.ProdutoReferencia ?? "",
      ),
    );
  }
  const nomes = full.concorrentesNomes ?? full.ConcorrentesNomes ?? [];
  return nomes.map((nome) => initialConcorrenteRow(nome, ""));
}
